import { useCallback, useEffect, useState } from 'react'
import type { CloudDispatchState } from '@/lib/cloud-dispatch'
import {
  dispatchHandoffToCloud,
  fetchCloudDispatchForDocument,
  isActiveCloudDispatch,
  isDocumentUuid,
} from '@/lib/cloud-dispatch'

type UseCloudDispatchOptions = {
  documentId: string | null | undefined
  filePath: string | null | undefined
  /** When primary path is a bet, try handoff path for dispatch lookup */
  fallbackFilePath?: string | null
  organizationId?: string | null
  /** When false, skip fetch (e.g. non-bet docs) */
  enabled?: boolean
}

export function useCloudDispatchForDocument({
  documentId,
  filePath,
  fallbackFilePath,
  organizationId,
  enabled = true,
}: UseCloudDispatchOptions) {
  const [dispatch, setDispatch] = useState<CloudDispatchState | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isDispatching, setIsDispatching] = useState(false)

  const refreshDispatch = useCallback(async () => {
    const id = documentId?.trim()
    const path = filePath?.trim()
    if ((!id && !path) || !enabled) {
      setDispatch(null)
      return
    }
    setIsLoading(true)
    try {
      const row = await fetchCloudDispatchForDocument(id || null, path || null, fallbackFilePath || null)
      setDispatch(row)
    } catch {
      /* non-critical */
    } finally {
      setIsLoading(false)
    }
  }, [documentId, filePath, fallbackFilePath, enabled])

  useEffect(() => {
    void refreshDispatch()
  }, [refreshDispatch])

  const dispatchToCloud = useCallback(
    async (targetRepo: 'flownotes' | 'flownotes-content' = 'flownotes') => {
      const path = filePath?.trim()
      const id = documentId?.trim()
      if (!path && !id) {
        return { success: false as const, error: 'No document path or id' }
      }
      setIsDispatching(true)
      try {
        const result = await dispatchHandoffToCloud({
          handoff_document_path: path || undefined,
          handoff_document_id: isDocumentUuid(id) ? id : undefined,
          target_repo: targetRepo,
          organizationId: organizationId ?? undefined,
          inboxOrganizationId: organizationId ?? undefined,
        })
        if (result.success && result.agentId) {
          setDispatch({
            documentId: id || '',
            title: result.title || '',
            filePath: path || '',
            agentId: result.agentId,
            dispatchedAt: result.dispatchedAt || new Date().toISOString(),
            dispatchStatus: 'running',
            threadId: result.threadId ?? null,
            isActive: true,
          })
          void refreshDispatch()
        } else if (result.error) {
          setDispatch((prev) =>
            prev
              ? { ...prev, error: result.error }
              : {
                  documentId: id || '',
                  title: result.title || '',
                  filePath: path || '',
                  agentId: '',
                  dispatchedAt: '',
                  dispatchStatus: 'error',
                  isActive: false,
                  error: result.error,
                }
          )
        }
        return result
      } finally {
        setIsDispatching(false)
      }
    },
    [documentId, filePath, organizationId, refreshDispatch]
  )

  const isActiveDispatch = isActiveCloudDispatch(dispatch)

  return {
    dispatch,
    isLoading,
    isDispatching,
    isActiveDispatch,
    dispatchToCloud,
    refreshDispatch,
    setDispatch,
  }
}
